import React from 'react';
import { Box, styled, Tab, Tabs, tabsClasses } from '@mui/material';

interface ICustomTabsProps {
    items: TCustomTab[];
    value?: number;
    onChange?: (value: number) => void;
}

export type TCustomTab = {
    title: string;
    content: React.FunctionComponent<any>;
    isError?: boolean;
};

export function DTabs(props: ICustomTabsProps) {
    const { items, value: controlledValue, onChange } = props;
    const [internalValue, setInternalValue] = React.useState(0);
    const value = controlledValue ?? internalValue;

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        if (controlledValue === undefined) setInternalValue(newValue);
        onChange?.(newValue);
    };

    return (
        <>
            <Tabs
                value={value}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons
                aria-label="visible arrows tabs example"
                sx={{
                    [`& .${tabsClasses.scrollButtons}`]: {
                        '&.Mui-disabled': { opacity: 0.0 },
                    },
                }}
            >
                {items.map((tab, index) => {
                    return (
                        <StyledTab
                            key={index}
                            label={tab.title}
                            isError={tab.isError}
                        />
                    );
                })}
            </Tabs>

            <Box
                sx={{
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    // Let this fill-and-scroll box shrink below its content so a
                    // tab that manages its own inner scroll (e.g. a table with a
                    // sticky header) doesn't push a second scrollbar onto the
                    // page. Inert where DTabs isn't inside a bounded flex column.
                    minHeight: 0,
                }}
            >
                {items.map((tab, index) => {
                    const Content = tab.content;
                    return (
                        <TabPanel key={index} value={value} index={index}>
                            <Content />
                        </TabPanel>
                    );
                })}
            </Box>
        </>
    );
}

interface TabPanelProps {
    children: React.ReactNode | React.ReactNode[];
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index } = props;

    return value === index ? (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            {children}
        </Box>
    ) : null;
}

const StyledTab = styled(Tab, {
    shouldForwardProp(propName: string) {
        return propName !== 'isError';
    },
})<Omit<TCustomTab, 'content' | 'title'>>(({ theme, isError }) => ({
    '&.Mui-selected': {
        color: isError ? theme.palette.error.main : undefined,
    },
    '&.MuiTab-root': {
        color: isError ? theme.palette.error.main : undefined,
    },
}));
